import { sha256 } from "js-sha256"
import { Capacitor, registerPlugin } from "@capacitor/core"
import { Device } from "@capacitor/device"
import { useOAuthService } from "@/services/OAuthService"
import { BlueIDAccess } from "@blueid/access-capacitor"
import { storeData, getStoredData, removeStoredItem } from "@/controllers/BaseController"
import { getCustomer } from "@/assets/Branding/branding.js"
import Toastify from "toastify-js"
import "toastify-js/src/toastify.css"

const IDENTITY_CACHE_KEY = "blueid_identity_id"
const BLUEID_SYNCED_KEY = "blueid_synced_once"
const BlueIDSecretsPlugin = registerPlugin("BlueIDSecretsPlugin")

// Toast helper
export function showToast(message, type = "default") {
  const background =
    {
      success: "#0f9d58",
      error: "#b00020",
      info: "#2196f3",
      warning: "#f9a825",
      default: "#333"
    }[type] || "#333"

  Toastify({
    text: message,
    duration: 3000,
    gravity: "bottom",
    position: "center",
    close: true,
    style: {
      background,
      color: "#fff",
      borderRadius: "8px",
      padding: "10px 18px",
      fontSize: "14px",
      fontWeight: "500",
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
    }
  }).showToast()
}

// Unique device hash
export async function getDeviceFingerprint() {
  const info = await Device.getInfo()
  const { sub = "", email = "" } = await useOAuthService().getUserProfile()
  const parts = [sub, email, info.platform, info.model, info.name, info.manufacturer, Capacitor.getPlatform()]
  const raw = parts.map(x => (x || "").toLowerCase()).join(":")
  const hash = sha256(raw)
  return parseInt(hash.slice(0, 12), 16).toString(36).padStart(7, "0").slice(0, 7)
}

// SDK claim step
async function claimIdentityViaSDK(activationToken) {
  const result = await BlueIDAccess.runCommand("claimIdentity", activationToken.trim())
  if (!result) throw new Error("Device could not be linked. Please try again.")
  console.log("🔐 Claimed identity via SDK:", result)
  return result
}

// Register or reuse identity
export async function registerAndClaimBlueIDIdentity() {
  try {
    showToast("🔐 Preparing secure access...", "info")

    const { name = "", email = "" } = await useOAuthService().getUserProfile()
    const fingerprint = await getDeviceFingerprint()
    const customer = await getCustomer()

    console.log(`[BlueID] Looking for identity for customer: ${customer}`)
    const { identities } = await BlueIDSecretsPlugin.listIdentities({ customer })
    let identity = identities.find(i => i.description === fingerprint)

    const userGroups = await getUserGroups()
    if (!userGroups.length) {
      showToast("⚠️ No access groups found for user.", "warning")
      throw new Error("User is not part of any BlueID access group.")
    }

    const accessObjects = await getUserGroupLocks()
    if (!accessObjects.length) {
      showToast("⚠️ No access points found for your groups.", "warning")
      throw new Error("No valid locks available for the user.")
    }

    if (!identity) {
      showToast("🆕 Setting up your device for access...", "info")

      const now = new Date()
      const validTo = new Date(now)
      validTo.setFullYear(validTo.getFullYear() + 1)

      const payload = {
        name: name || email || fingerprint,
        description: fingerprint,
        email: email || "mobile@pinestack.com",
        mobilePhone: "+490000000000",
        role: "regular",
        validFrom: now.toISOString(),
        validTo: validTo.toISOString(),
        refresh: 30,
        ephemeral: false,
        groups: userGroups.map(g => ({
          _id: g._id,
          key: g.key,
          accessType: g.accessType || "default",
          name: g.name || "Pinestack Group"
        })),
        objects: accessObjects.map(o => ({
          _id: o._id,
          name: o.name,
          accessType: "default"
        })),
        schedules: [],
        externalGroupId: null
      }

      identity = await BlueIDSecretsPlugin.createIdentity({ customer, payload })
      showToast("✅ Device registered. Proceeding to activation...", "success")
    } else {
      showToast("🔁 Device already registered. Verifying access...", "info")
      console.log("🔍 Reusing existing identity:", identity)
    }

    if (identity.status !== "activated") {
      showToast("🔑 Linking device to access system...", "info")
      const { activationToken } = await BlueIDSecretsPlugin.renewActivationToken({ customer, identityId: identity._id })
      showToast("Claiming identity...", "info")
      await new Promise(resolve => setTimeout(resolve, 3000))
      await claimIdentityViaSDK(activationToken)
      showToast("✅ Device is now linked and ready to use.", "success")
    } else {
      showToast("✅ Device is already activated for access.", "success")
    }

    await storeData("blueid_identity_id", identity._id)
    return { success: true, identityId: identity._id }
  } catch (err) {
    console.error("❌ Identity registration error:", err)
    showToast("❌ Setup failed: " + (err.message || "Unexpected error"), "error")
    return { success: false, error: err.message }
  }
}

// Unlock a device using BlueID
export async function tryUnlockBlueIDDevice(deviceId) {
  if (!deviceId) return { success: false, error: "Missing device ID" }

  try {
    let { identityId } = (await getStoredData("blueid_identity")) || {}
    const customer = await getCustomer()

    if (!identityId) {
      const fingerprint = await getDeviceFingerprint()
      const { identities } = await BlueIDSecretsPlugin.listIdentities({ customer })
      const identity = identities.find(i => i.description === fingerprint)
      if (!identity) throw new Error("This device is not yet set up for access.")

      identityId = identity._id
      await storeData("blueid_identity", { identityId })
    }

    const result = await BlueIDAccess.runCommand("tryAccessDevice", identityId, deviceId)
    if (result) {
      return { success: true, result }
    } else {
      throw new Error("No valid response from lock")
    }
  } catch (err) {
    console.error("❌ Unlock failed:", err)
    showToast(`❌ Unlock failed: ${err.message}`, "error")
    return { success: false, error: err.message }
  }
}

// Fetch all locks for current identity
export async function getBlueIDLocks() {
  try {
    const identity = await getStoredData(IDENTITY_CACHE_KEY)
    if (!identity || (typeof identity !== "string" && !identity._id)) {
      throw new Error("No device identity found.")
    }

    const identityId = typeof identity === "string" ? identity : identity._id
    const { objects = [] } = (await BlueIDAccess.runCommand("getAccessObjects", identityId)) || []
    const filtered = objects.filter(o => Array.isArray(o.deviceIds) && o.deviceIds.length > 0)

    showToast(`🔍 ${filtered.length} access points ready.`, "info")
    return filtered
  } catch (err) {
    console.error("[BlueID] Failed to fetch locks:", err)
    showToast("❌ Unable to fetch access points.", "error")
    return []
  }
}

export async function getUserGroupLocks() {
  try {
    const { group } = await useOAuthService().getUserProfile()
    if (!Array.isArray(group) || group.length === 0) {
      showToast("ℹ️ No user groups found.", "info")
      return []
    }

    const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi
    const groupIds = group.flatMap(g => g.match(uuidRegex) || [])

    if (groupIds.length === 0) {
      showToast("ℹ️ No valid group IDs found.", "info")
      return []
    }
    const customer = await getCustomer()

    let { objects } = await BlueIDSecretsPlugin.getLocks({ customer })
    if (!objects || !Array.isArray(objects)) {
      showToast("❌ Unable to fetch locks for user groups.", "error")
      return []
    }
    // Filter locks: keep only those where at least one group._id matches a groupId
    let blueIdObjects = []
    for (let obj of objects) {
      let groups = obj.groups || []
      groups.forEach(group => {
        let groupName = group.name
        let isGroupMatch = groupName && groupIds.some(id => groupName.includes(id))
        if (isGroupMatch) {
          console.log("isGroupMatch:", isGroupMatch)
          blueIdObjects.push(obj)
        }
      })
    }

    console.log("🔍 Fetched locks for user groups:", blueIdObjects)
    return objects
  } catch (error) {
    console.error("❌ Failed to fetch user groups:", error)
    return []
  }
}

// Sync identities once
export async function syncBlueIDIdentities() {
  try {
    const alreadySynced = await getStoredData(BLUEID_SYNCED_KEY)
    if (alreadySynced) {
      console.log("[BlueID] Already synced.")
      return { success: true, skipped: true }
    }

    showToast("🔄 Syncing access permissions...", "info")
    await BlueIDAccess.runCommand("synchronizeIdentities")
    await storeData(BLUEID_SYNCED_KEY, true)
    showToast("✅ Permissions synced successfully.", "success")
    return { success: true, skipped: false }
  } catch (err) {
    console.error("❌ Sync failed:", err)
    showToast(`❌ Sync failed: ${err.message}`, "error")
    return { success: false, error: err.message }
  }
}

export const getUserGroups = async () => {
  try {
    const { group } = await useOAuthService().getUserProfile()
    if (!Array.isArray(group) || group.length === 0) {
      showToast("ℹ️ No user groups found.", "info")
      return []
    }

    const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi
    const groupIds = group.flatMap(g => g.match(uuidRegex) || [])

    if (groupIds.length === 0) {
      showToast("ℹ️ No valid group IDs found.", "info")
      return []
    }

    const customer = await getCustomer()

    const results = await Promise.allSettled(groupIds.map(id => BlueIDSecretsPlugin.getGroups({ customer, searchTerm: id })))

    const userGroups = results.filter(r => r.status === "fulfilled" && r.value?.groups?.length).flatMap(r => r.value.groups)

    console.log("✅ User groups:", userGroups)
    return userGroups
  } catch (error) {
    console.error("❌ Failed to fetch user groups:", error)
    showToast("❌ Unable to fetch user groups.", "error")
    return []
  }
}

// Revoke identity
export async function revokeBlueIDIdentityAndClearCache() {
  try {
    const fingerprint = await getDeviceFingerprint()
    const customer = await getCustomer()
    const { identities } = await BlueIDSecretsPlugin.listIdentities({ customer })
    const identity = identities.find(i => i.description === fingerprint)

    if (!identity) {
      // showToast('This device is not registered for access.', 'info')
      return
    }

    const result = await BlueIDSecretsPlugin.revokeIdentity({ customer, identityId: identity._id })

    if (result?.success) {
      await removeStoredItem("blueid_identity_id")
      showToast("Access removed from this device.", "success")
    } else {
      showToast("⚠️ Revocation attempted but uncertain result.", "warning")
    }
  } catch (err) {
    console.error("❌ Revoke failed:", err)
    showToast(`❌ Failed to remove access: ${err.message}`, "error")
  }
}
